

















































































import Vue from 'vue';
import { chain, cloneDeep, fromPairs, get, isEqual } from 'lodash';
import $ from 'jquery';

import { format } from 'date-fns';
import { isVisible, isAnswered, isCheckboxOrRadio, validate } from './helpers';
import { Question } from '../types';

import AdditionalText from './fields/AdditionalText.vue';
import Checkbox from './fields/Checkbox.vue';
import DateField from './fields/Date.vue';
import DateTime from './fields/DateTime.vue';
import Email from './fields/Email.vue';
import File from './fields/File.vue';
import Navigation from './Navigation.vue';
import ProgressBar from './ProgressBar.vue';
import Radio from './fields/Radio.vue';
import SurveySummary from './SurveySummary.vue';
import TextLong from './fields/TextLong.vue';
import TextShort from './fields/TextShort.vue';
import Url from './fields/Url.vue';

/* eslint-disable @typescript-eslint/camelcase */
enum TYPE_COMPONENTS {
  additional_text = 'AdditionalText',
  checkboxes = 'Checkbox',
  date = 'DateField',
  datetime = 'DateTime',
  email = 'Email',
  file_upload = 'File',
  radio = 'Radio',
  rich_text = 'TextLong',
  text_field = 'TextShort',
  url = 'Url',
}
/* eslint-enable camelcase */

export default Vue.extend({
  components: {
    AdditionalText,
    Checkbox,
    DateField,
    DateTime,
    Email,
    File,
    Navigation,
    ProgressBar,
    Radio,
    SurveySummary,
    TextLong,
    TextShort,
    Url,
  },
  props: {
    apiGetUrl: {
      type: String,
      default: null,
    },
    apiPostUrl: {
      type: String,
      default: null,
    },
    apiUploadFileUrl: {
      type: String,
      default: null,
    },
    apiDeleteFileUrl: {
      type: String,
      default: null,
    },
    csrfToken: {
      type: String,
      default: null,
    },
    successUrl: {
      type: String,
      default: null,
    },
    surveyId: {
      type: String,
      default: null,
    },
    preview: {
      type: Boolean,
      default: null,
    },
    previewData: {
      type: Object,
      default: null,
    },
  },
  provide() {
    return {
      csrfToken: this.csrfToken,
      apiUploadFileUrl: this.apiUploadFileUrl,
      apiDeleteFileUrl: this.apiDeleteFileUrl,
    };
  },
  data() {
    return {
      currentPageIndex: <number>0,
      surveyData: {
        company_name: <string>'',
        questions: <Question[]>{},
        title: <string>'',
      },
      questionsMap: {},
      optionsMap: {},
      loading: false,
      savedAt: '',
      uploadInProgress: false,
      dirtyQuestions: {},
      autosaveInterval: <number>-1,
      lastSaveData: {},
    };
  },
  computed: {
    pages(): Question[][] {
      const questions: Question[] = get(this.surveyData, 'questions');

      if (questions) {
        return chain(questions)
          .filter(this.isVisible)
          .sortBy('rank')
          .groupBy('page_number')
          .values()
          .value();
      }
      return [];
    },
    postData(): object {
      return fromPairs(
        (this.surveyData.questions as Question[]).filter(isAnswered).map(question => {
          if (isCheckboxOrRadio(question)) {
            const pickedOptions = question.options.filter(option => option.picked).map(option => ({ id: option.id }));

            return [question.id, pickedOptions];
          } else {
            return [question.id, question.text];
          }
        })
      );
    },
    allQuestionsValid(): boolean {
      return this.getInvalidQuestions().length === 0;
    },
  },
  created() {
    this.toggleAutosave(true);
    const page = this.pageHash();
    this.fetchSurveyData().then(response => {
      this.setSurveyData(response);
      this.populateDirty();
      this.goToPage(page);
    });
  },
  destroyed() {
    this.toggleAutosave(false);
  },
  methods: {
    setSurveyData(surveyData: any) {
      this.surveyData = surveyData;

      const questionsMap = {};
      const optionsMap = {};

      surveyData.questions.forEach((question: Question) => {
        questionsMap[question.id] = question;

        question.options.forEach(option => {
          optionsMap[option.id] = option;
        });
      });

      // if we used this.questionsMap directly we'd need to use
      // Vue.set(questionsMap, question.id, question) instead of
      // questionsMap[question.id] = question (Vue change detection problems)
      this.questionsMap = questionsMap;
      this.optionsMap = optionsMap;
    },
    getInvalidQuestions(): Question[] {
      return this.surveyData.questions.filter(
        question =>
          this.isVisible(question) &&
          !validate({
            type: question.type,
            required: question.required,
            text: question.text,
            options: question.options,
          })
      );
    },
    fetchSurveyData() {
      return this.previewData ? Promise.resolve(this.previewData) : $.get(this.apiGetUrl);
    },
    goToPage(pageNumber: number, scrollTarget?: string) {
      if (pageNumber === this.pages.length + 1 && !this.preview) {
        // Clicked submit on last page
        this.save(true).then(
          () => {
            this.redirectToSuccess();
          },
          error => {
            // TODO: TEMP throw error instead
            // eslint-disable-next-line no-console
            console.error(error);
            // survey was not saved
          }
        );
      } else {
        this.currentPageIndex = Math.min(this.pages.length, Math.max(0, pageNumber));
        this.pageHash(this.currentPageIndex + 1);
        this.scrollPage(scrollTarget);
      }
    },
    goToQuestion(question: Question) {
      this.populateDirty(true);
      this.goToPage(question.page_number - 1, `#question-${question.id}`);
    },
    save(finalSubmit = false, forceSave = false, partialSave = false) {
      if ((this.preview || (!finalSubmit && isEqual(this.lastSaveData, this.postData))) && !forceSave) {
        return Promise.resolve();
      }

      this.lastSaveData = cloneDeep(this.postData);
      this.loading = true;
      return $.ajax({
        type: 'POST',
        url: this.apiPostUrl,
        contentType: 'application/json',
        data: JSON.stringify({
          finalSubmit,
          partialSave,
          data: this.postData,
        }),
        headers: { 'X-CSRF-Token': this.csrfToken },
        dataType: 'json',
      }).then(res => {
        this.loading = false;

        if (!res.status) {
          window.GGRC.Notify({
            error: 'Failed to save questionnaire data.',
          });

          throw new Error('Failed to save questionnaire data');
        } else {
          this.savedAt = format(new Date(), 'h:mm');
        }
      });
    },
    updateAnswers(id: number, data: object | string) {
      const question = this.questionsMap[id];
      if (question == null) {
        return;
      }
      const attr = data && typeof data === 'object' ? 'options' : 'text';

      Vue.set(question, attr, data);

      if (attr === 'options') {
        data.forEach(option => {
          Vue.set(this.optionsMap, option.id, option);
        });
      }
    },
    updateUploadStatus(newStatus: boolean) {
      this.uploadInProgress = newStatus;
    },
    isVisible(question: Question) {
      return isVisible(question, this.questionsMap, this.optionsMap);
    },
    getComponentForType(type: keyof typeof TYPE_COMPONENTS) {
      return TYPE_COMPONENTS[type] as string;
    },
    redirectToSuccess() {
      window.location.href = this.successUrl;
    },
    setDirty(questionId: number) {
      this.dirtyQuestions = {
        ...this.dirtyQuestions,
        [questionId]: true,
      };
    },
    populateDirty(dirtiness = false) {
      this.dirtyQuestions = this.surveyData.questions.reduce(
        (questions, question) => ({ ...questions, [question.id]: dirtiness }),
        {}
      );
    },
    pageHash(index?: number) {
      if (index) {
        location.hash = `#page=${index}`;
      }

      const hash = location.hash;
      const pageNo = parseInt(hash.split('=')[1]);
      if (!hash.includes('page=') || isNaN(pageNo)) {
        return 0;
      }

      // Page hash is 1 index based
      return pageNo - 1;
    },
    scrollPage(target?: string) {
      let offset = 0;

      // Wait for HTML to render
      this.$nextTick().then(() => {
        if (target) {
          offset = $(target)[0].offsetTop;
        }

        $('.main-content').scrollTop(offset);
      });
    },
    toggleAutosave(newState: boolean) {
      const AUTOSAVE_DELAY = 10000;

      if (newState) {
        this.autosaveInterval = setInterval(this.save, AUTOSAVE_DELAY, false, false, true);
      } else {
        clearInterval(this.autosaveInterval);
      }
    },
  },
});
